import { ConvexError } from 'convex/values';
import { toast } from 'sonner';

export type RealtimeConversationActivityKind = 'user' | 'agent';

export type RealtimeLeaseRenewal = {
  serverNow: number;
  leaseExpiresAt: number;
  leaseDurationMs: number;
  /** The server already ended this lease (e.g. a beat queued behind a drop). */
  ended?: true;
};

type LivenessReporterArgs = {
  heartbeat: (
    activityKind: RealtimeConversationActivityKind | undefined
  ) => Promise<RealtimeLeaseRenewal>;
  isCurrentCall: () => boolean;
  onRenewed: (leaseDurationMs: number) => void;
  /** The server already ended this lease; end the call once, without retrying. */
  onRejected: () => void;
  onError?: (error: unknown) => void;
  monotonicNow?: () => number;
  intervalMs?: number;
  retryMs?: number;
};

// Three beats fit in the server's 90 s liveness window.
export const REALTIME_LEASE_BEAT_INTERVAL_MS = 30_000;
const DEFAULT_BEAT_RETRY_MS = 10_000;

/**
 * A lease that ended without a hang-up (expired during a network drop, or
 * refused on reconnect) closes the call like a hang-up, not a sticky error.
 */
const endedCalls = new WeakSet<object>();

export function endExpiredRealtimeVoiceCall(args: {
  isCurrentCall: () => boolean;
  voiceCopy: { connectionError: string };
  onLeaseEnded: () => void;
}): void {
  // The lease timer and a refused beat can both fire; notify and end once.
  if (!args.isCurrentCall() || endedCalls.has(args)) return;
  endedCalls.add(args);
  toast.error(args.voiceCopy.connectionError);
  args.onLeaseEnded();
}

export function isRealtimeLeaseRejection(error: unknown): boolean {
  return (
    error instanceof ConvexError &&
    (error.data === 'Voice session expired' || error.data === 'Access denied')
  );
}

/**
 * One beat per interval proves the client is alive and carries any conversation
 * activity seen since the last beat, so a call never runs two heartbeats.
 */
export function createRealtimeLeaseLivenessReporter(
  args: LivenessReporterArgs
): {
  markActivity: (activityKind: RealtimeConversationActivityKind) => void;
  tick: () => void;
} {
  const monotonicNow =
    args.monotonicNow ?? (() => globalThis.performance.now());
  const intervalMs = args.intervalMs ?? REALTIME_LEASE_BEAT_INTERVAL_MS;
  const retryMs = args.retryMs ?? DEFAULT_BEAT_RETRY_MS;
  let lastBeatAt: number | null = null;
  let pendingActivity: RealtimeConversationActivityKind | undefined;
  let inFlight = false;
  let rejected = false;

  return {
    markActivity(activityKind) {
      pendingActivity = activityKind;
    },
    tick() {
      if (inFlight || rejected || !args.isCurrentCall()) return;
      const now = monotonicNow();
      if (lastBeatAt !== null && now - lastBeatAt < intervalMs) return;
      lastBeatAt = now;
      inFlight = true;
      const activityKind = pendingActivity;
      pendingActivity = undefined;
      void args
        .heartbeat(activityKind)
        .then((renewal) => {
          if (!args.isCurrentCall()) return;
          if (renewal.ended) {
            rejected = true;
            args.onRejected();
            return;
          }
          if (
            !Number.isFinite(renewal.leaseDurationMs) ||
            renewal.leaseDurationMs < 0
          ) {
            return;
          }
          args.onRenewed(
            getRealtimeLeaseRemainingDurationMs({
              leaseDurationMs: renewal.leaseDurationMs,
              requestStartedAt: now,
              now: monotonicNow(),
            })
          );
        })
        .catch((error: unknown) => {
          if (!args.isCurrentCall()) return;
          if (isRealtimeLeaseRejection(error)) {
            rejected = true;
            args.onRejected();
            return;
          }
          pendingActivity ??= activityKind;
          lastBeatAt = monotonicNow() - intervalMs + retryMs;
          args.onError?.(error);
        })
        .finally(() => {
          inFlight = false;
        });
    },
  };
}

export function getRealtimeLeaseRemainingDurationMs(args: {
  leaseDurationMs: number;
  requestStartedAt: number;
  now: number;
}): number {
  const elapsedMs = Math.max(0, args.now - args.requestStartedAt);
  return Math.max(0, args.leaseDurationMs - elapsedMs);
}

export function shouldReportRealtimeUserActivityImmediately(args: {
  acceptedSpeech: boolean;
  pttActive: boolean;
}): boolean {
  return args.acceptedSpeech && args.pttActive;
}

export function getRealtimeTransportConversationActivity(args: {
  eventType: string;
  responseTicketKind?: string;
}): RealtimeConversationActivityKind | null {
  if (
    args.eventType === 'response.created' &&
    args.responseTicketKind === 'user_speech'
  ) {
    return 'user';
  }
  if (
    args.eventType === 'response.done' ||
    args.eventType === 'output_audio_buffer.started'
  ) {
    return 'agent';
  }
  return null;
}

export function reportRealtimeTransportConversationActivity(args: {
  eventType: string;
  responseTicketKind?: string;
  reportActivity: (activityKind: RealtimeConversationActivityKind) => void;
}): void {
  const activityKind = getRealtimeTransportConversationActivity(args);
  if (activityKind) args.reportActivity(activityKind);
}
